import React, { useEffect, useState, useCallback, useLayoutEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { usePromise } from 'react-use'
import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { en, fr, es, cs, pl, de } from 'make-plural/plurals'

import LocalisationContext from './LocalisationContext'
import { logger } from './logger'
import { QS_PARAM_NAME } from './query-param-identifiers'
import SkinContext from '@yoti-web-share/skins/src/SkinContext'

const defaultLocale = 'en';

const supportedPlurals = {
  en, fr, es, cs, pl, de
}

const getShortLocale = (locale) => locale.substring(0, 2).toLowerCase()

function loadLocaleData(locale) {
  const localePrefix = getShortLocale(locale)
  const pluralsRule = supportedPlurals[localePrefix]
  if (pluralsRule) {
    i18n.loadLocaleData(locale, {plurals: pluralsRule})
  } else {
    logger.warn(`Could not find plurals rule for locale '${locale}' using prefix '${localePrefix}'!`)
  }
}

let lastLocaleRequested = ''
async function dynamicActivate(locale) {
  lastLocaleRequested = locale
  try {
    const { messages } = await import(`$@-LOCALES_PATH/${locale}/messages`)
    if (lastLocaleRequested === locale) {
      loadLocaleData(locale)
      i18n.load(locale, messages)
      i18n.activate(locale)
      document.documentElement.lang = getShortLocale(locale);
    }
  } catch (e) {
    logger.warn(`Could not find locale '${locale}'.`)
    const isLongFormat = locale.length > 2
    if(isLongFormat) {
      const shortLocale = getShortLocale(locale)
      logger.warn(`Defaulting locale to '${shortLocale}'.`)
      await dynamicActivate(shortLocale)
    } else {
      dynamicActivate(defaultLocale).catch((e)=>{
        logger.warn(`Dynamic locale loading issue`, e)
      })
    }
  }
}

const LocalisationProvider = ({ children, locale, renderBeforeLocaleLoaded }) => {
  const initialLocale = locale || new URLSearchParams(window.location.search).get(QS_PARAM_NAME) || defaultLocale

  const safeAsync = usePromise()
  const [internalLocale, setInternalLocale] = useState(initialLocale)
  const [initialLocaleLoaded, setInitialLocaleLoaded] = useState(false)

  const changeLocale = useCallback((newLocale) => {
    setInternalLocale(newLocale)
  }, [])

  useLayoutEffect(() => {
    logger.log(`[LocalisationProvider] useLayoutEffect>>>>`, locale, `<<<< locale`)
    if(locale) setInternalLocale(locale)
  }, [locale])

  useEffect(() => {
    safeAsync(dynamicActivate(internalLocale))
      .then(()=>{
        setInitialLocaleLoaded(true)
        logger.info('[LocalisationProvider] Loaded internalLocale:', internalLocale)
      })
      .catch((e) => {
        setInitialLocaleLoaded(true)
        logger.warn('[LocalisationProvider] Could not change locale, encountered error', e)
      })
  }, [internalLocale])

  const context = {
    defaultLocale,
    locale,
    changeLocale
  }

  return (
    <LocalisationContext.Provider value={context}>
      <I18nProvider i18n={i18n} forceRenderOnLocaleChange={false}>
        {(initialLocaleLoaded || renderBeforeLocaleLoaded) && children}
      </I18nProvider>
    </LocalisationContext.Provider>
  )
}

LocalisationProvider.defaultProps = {
  children: null,
  locale: undefined,
  renderBeforeLocaleLoaded: false
}

LocalisationProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element
  ]),
  locale: PropTypes.string,
  renderBeforeLocaleLoaded: PropTypes.bool,
}

export default LocalisationProvider
